<script setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useElementSize } from '@vueuse/core';
import BackButton from '../BackButton.vue';
import InboxName from '../InboxName.vue';
import MoreActions from './MoreActions.vue';
import Avatar from 'next/avatar/Avatar.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import wootConstants from 'dashboard/constants/globals';
import { conversationListPageURL } from 'dashboard/helper/URLHelper';
import { snoozedReopenTime } from 'dashboard/helper/snoozeHelpers';
import { useInbox } from 'dashboard/composables/useInbox';
import { useI18n } from 'vue-i18n';
import { useUISettings } from 'dashboard/composables/useUISettings';

// NOTICE: KAPTION-INTEGRATION

const props = defineProps({
  chat: {
    type: Object,
    default: () => ({}),
  },
  showBackButton: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const store = useStore();
const route = useRoute();
const conversationHeader = ref(null);
const { width } = useElementSize(conversationHeader);
const { isAWebWidgetInbox } = useInbox();
const { updateUISettings, uiSettings } = useUISettings();

const currentChat = computed(() => store.getters.getSelectedChat);
const accountId = computed(() => store.getters.getCurrentAccountId);

const chatMetadata = computed(() => props.chat.meta);

const backButtonUrl = computed(() => {
  const {
    params: { inbox_id: inboxId, label, teamId, id: customViewId },
    name,
  } = route;

  const conversationTypeMap = {
    conversation_through_mentions: 'mention',
    conversation_through_unattended: 'unattended',
  };
  return conversationListPageURL({
    accountId: accountId.value,
    inboxId,
    label,
    teamId,
    conversationType: conversationTypeMap[name],
    customViewId,
  });
});

const isHMACVerified = computed(() => {
  if (!isAWebWidgetInbox.value) {
    return true;
  }
  return chatMetadata.value.hmac_verified;
});

const currentContact = computed(() =>
  store.getters['contacts/getContact'](props.chat.meta.sender.id)
);

// NOTICE: KAPTION-INTEGRATION - Add phone number computed property
const currentContactPhoneNumber = computed(() => {
  // Try to get phone number from conversation meta first, then from store
  // Note: phone number is snake_case in the data structure
  return (
    chatMetadata.value.sender?.phone_number ||
    currentContact.value?.phone_number ||
    ''
  );
});

const isSnoozed = computed(
  () => currentChat.value.status === wootConstants.STATUS_TYPE.SNOOZED
);

const isMuted = computed(() => !!currentChat.value?.muted);

const mutedDisplayText = computed(() => {
  const mutedUntil = currentChat.value?.muted_until;
  if (mutedUntil === 'until_resolved') {
    return t('CONVERSATION.HEADER.MUTED_UNTIL_RESOLVED');
  }
  if (mutedUntil) {
    return `${t('CONVERSATION.HEADER.MUTED_UNTIL')} ${snoozedReopenTime(mutedUntil)}`;
  }
  return t('CONVERSATION.HEADER.MUTED_FOREVER');
});

const snoozedDisplayText = computed(() => {
  const { snoozed_until: snoozedUntil } = currentChat.value;
  if (snoozedUntil) {
    return `${t('CONVERSATION.HEADER.SNOOZED_UNTIL')} ${snoozedReopenTime(snoozedUntil)}`;
  }
  return t('CONVERSATION.HEADER.SNOOZED_UNTIL_NEXT_REPLY');
});

const inbox = computed(() => {
  const { inbox_id: inboxId } = props.chat;
  return store.getters['inboxes/getInbox'](inboxId);
});

const hasMultipleInboxes = computed(
  () => store.getters['inboxes/getInboxes'].length > 1
);

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

// NOTICE: KAPTION-INTEGRATION - Add campaign computed property
const campaignTitle = computed(() => {
  // Campaign data can be in chat.campaign or chat.meta.campaign depending on API response structure
  return props.chat.campaign?.title || props.chat.meta?.campaign?.title;
});

const hasCampaign = computed(() => !!campaignTitle.value);

// NOTICE: KAPTION-INTEGRATION
const toggleContactSidebar = () => {
  // Default to true if undefined, then toggle
  const currentState = uiSettings.value.is_contact_sidebar_open ?? true;
  updateUISettings({
    is_contact_sidebar_open: !currentState,
    is_copilot_panel_open: false,
  });
};
</script>

<template>
  <div
    ref="conversationHeader"
    class="flex flex-col gap-3 items-center justify-between flex-1 w-full min-w-0 xl:flex-row px-3 py-2 border-b bg-n-background border-n-weak h-24 xl:h-12"
  >
    <div
      class="flex items-center justify-start w-full xl:w-auto max-w-full min-w-0 xl:flex-1"
    >
      <BackButton
        v-if="showBackButton"
        :back-url="backButtonUrl"
        class="ltr:mr-2 rtl:ml-2"
      />
      <!-- NOTICE: KAPTION-INTEGRATION - Make entire contact section clickable -->
      <div
        class="flex items-center cursor-pointer hover:bg-n-alpha-1 rounded-lg p-1 -m-1 transition-colors"
        @click="toggleContactSidebar"
      >
        <Avatar
          :name="currentContact.name"
          :src="currentContact.thumbnail"
          :size="32"
          :status="currentContact.availability_status"
          hide-offline-status
          rounded-full
        />
        <div
          class="flex flex-col items-start min-w-0 ml-2 overflow-hidden rtl:ml-0 rtl:mr-2"
        >
          <div class="flex flex-row items-center max-w-full gap-1 p-0 m-0">
            <span
              class="text-sm font-medium truncate leading-tight text-n-slate-12"
            >
              {{ currentContact.name }}
            </span>
            <fluent-icon
              v-if="!isHMACVerified"
              v-tooltip="$t('CONVERSATION.UNVERIFIED_SESSION')"
              size="14"
              class="text-n-amber-10 my-0 mx-0 min-w-[14px] flex-shrink-0"
              icon="warning"
            />
            <!-- NOTICE: KAPTION-INTEGRATION - Add phone number to the right with same height and alignment -->
            <span
              v-if="currentContactPhoneNumber"
              class="text-sm font-medium truncate leading-tight text-n-slate-10 ml-2"
            >
              {{ currentContactPhoneNumber }}
            </span>
          </div>

          <div
            class="flex items-center gap-2 overflow-hidden text-xs conversation--header--actions text-ellipsis whitespace-nowrap"
          >
            <InboxName v-if="hasMultipleInboxes" :inbox="inbox" class="!mx-0" />
            <!-- NOTICE: KAPTION-INTEGRATION - Add campaign badge -->
            <span
              v-if="hasCampaign"
              class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-n-amber-1 text-n-amber-11 border border-n-amber-3"
            >
              <fluent-icon icon="megaphone" size="12" class="mr-1" />
              {{ campaignTitle }}
            </span>
            <span v-if="isSnoozed" class="font-medium text-n-amber-10">
              {{ snoozedDisplayText }}
            </span>
            <span v-if="isMuted" class="font-medium text-n-slate-10">
              {{ mutedDisplayText }}
            </span>
          </div>
        </div>
      </div>
    </div>
    <div
      class="flex flex-row items-center justify-start xl:justify-end flex-shrink-0 gap-2 w-full xl:w-auto header-actions-wrap"
    >
      <SLACardLabel
        v-if="hasSlaPolicyId"
        :chat="chat"
        show-extended-info
        :parent-width="width"
        class="hidden md:flex"
      />
      <MoreActions :conversation-id="currentChat.id" />
    </div>
  </div>
</template>
